import { ShaderChunk } from "three";
import { ATMOSPHERE_UNIFORMS, HASH } from "./common.ts";
import { SURF } from "./surf.ts";

/**
 * Registro dos trechos GLSL compartilhados (F51): cada trecho tem um id, os trechos de que depende e
 * o código de cada estágio. `assemble` junta uma lista com as dependências antes, sem repetir nenhum
 * (cada um entra uma vez por shader, marcado com `// @chunk <id>`). Os shaders da ilha (terreno,
 * capim, mar, céu, redemoinho) e o material de superfície (`surface.ts`) saem daqui; os opcionais
 * ficam sob `#ifdef`, com a define ligada pelo material.
 */
export interface Chunk {
  id: ChunkId;
  deps: readonly ChunkId[];
  vertex?: string;
  fragment?: string;
}

export type Stage = "vertex" | "fragment";

export type ChunkId = "atmo" | "hash" | "noise" | "ramp3" | "keyFill" | "rimNight" | "fogGlow" | "wind" | "dither" | "nearFade" | "farFade" | "palettes" | "blobs" | "blobVertex" | "blobFragment" | "outline" | "glow" | "dissolve" | "skin" | "style" | "styleCore" | "energy" | "surf" | "ringLight" | "sunShadow";

/** Uniforms da atmosfera (os de antes da F51; os do estilo ficam nos próprios trechos). */
const atmo = { id: "atmo", deps: [], vertex: ATMOSPHERE_UNIFORMS, fragment: ATMOSPHERE_UNIFORMS } satisfies Chunk;

const hash = { id: "hash", deps: [], vertex: HASH, fragment: HASH } satisfies Chunk;

/**
 * Ruído de valor (F93): o `hash12` nos cantos da célula, misturado pela curva suave. O fogo e as brasas
 * dos perigos, o redemoinho, a parede do Cerco e as chamas do `fumaca` usavam cada um a sua cópia.
 */
const NOISE = /* glsl */ `
float noise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash12(i), hash12(i + vec2(1.0, 0.0)), u.x), mix(hash12(i + vec2(0.0, 1.0)), hash12(i + vec2(1.0, 1.0)), u.x), u.y);
}
`;
const noise = { id: "noise", deps: ["hash"], vertex: NOISE, fragment: NOISE } satisfies Chunk;

/**
 * Rampa toon de 3 faixas (sombra, meia-luz, luz) antisserrilhada por `fwidth`. `uRamp`: bordas das
 * faixas (x, y), peso da primeira (z) e o piso da sombra (w). A largura vem de `rampWidth`, chamada
 * no começo do `main` com as outras derivadas.
 */
const ramp3 = {
  id: "ramp3",
  deps: [],
  fragment: /* glsl */ `
uniform vec4 uRamp;
float rampWidth(float ndl) {
  return fwidth(ndl) * 1.5 + 0.02;
}
float toonRamp(float ndl, float w) {
  float band = uRamp.z * smoothstep(uRamp.x - w, uRamp.x + w, ndl) + (1.0 - uRamp.z) * smoothstep(uRamp.y - w, uRamp.y + w, ndl);
  return mix(uRamp.w, 1.0, band) * clamp(ndl * 0.6 + 0.4, 0.0, 1.0);
}
`,
} satisfies Chunk;

/**
 * Luz quente do sol (ou da lua) na rampa e preenchimento frio: ambiente hemisférico (céu em cima,
 * chão embaixo) mais uma luz fria do céu vinda do lado oposto ao sol, com força `uFill.x` e
 * inclinação `uFill.y` (a componente vertical da direção, antes de normalizar), só no que o sol não
 * alcança (some com `ndl` ≥ 0,25: o chão iluminado não esfria). A sombra
 * fica tingida de frio, nunca preta (doc 05, regra 4). `toonAmbient` é o que sobra sem o sol (a cor
 * das sombras-blob).
 */
const keyFill = {
  id: "keyFill",
  deps: ["atmo", "ramp3"],
  fragment: /* glsl */ `
uniform vec4 uFill;
vec3 toonAmbient(vec3 normal, float ndl) {
  vec3 ambient = mix(uAmbientGround, uAmbientSky, normal.y * 0.5 + 0.5);
  vec3 fillDir = normalize(vec3(-uLightDir.x, max(uFill.y, 0.05), -uLightDir.z));
  float shade = clamp(0.5 - ndl * 2.0, 0.0, 1.0);
  return ambient * 0.55 + uAmbientSky * (uFill.x * clamp(dot(normal, fillDir), 0.0, 1.0) * shade);
}
vec3 toonKey(float ndl, float w) {
  return uLightColor * toonRamp(ndl, w) * 0.85;
}
`,
} satisfies Chunk;

/**
 * Contorno de luz: o de sempre (cor do sol, força `uRim` do material, doc 05 regra 5) e o noturno,
 * frio, só nas assombrações (`uRimNightOn`), que cresce com a noite: `uRimNight` traz a cor (rgb) e
 * a força (w).
 */
const rimNight = {
  id: "rimNight",
  deps: ["atmo"],
  fragment: /* glsl */ `
uniform float uRim;
uniform float uRimNightOn;
uniform vec4 uRimNight;
vec3 rimLight(vec3 normal, vec3 view) {
  float edge = pow(1.0 - clamp(dot(normal, view), 0.0, 1.0), 3.0);
  vec3 day = uLightColor * (edge * uRim) * 0.35;
  return day + uRimNight.rgb * (edge * uRimNight.w * uNight * uRimNightOn);
}
`,
} satisfies Chunk;

/**
 * Neblina exponencial ao quadrado, mais densa perto do nível do mar, na cor `fogTint`: a da
 * neblina, clareada na direção do sol pelo brilho `uSunGlow` (x: força, y: expoente), que some à
 * noite. O céu usa a mesma cor na faixa do horizonte. `seaHue`: o matiz da água (luminância 1), o do
 * ambiente agora (névoa e horizonte) com um resto de verde-azulado — o mar e o chão submerso.
 */
const fogGlow = {
  id: "fogGlow",
  deps: ["atmo"],
  fragment: /* glsl */ `
uniform vec4 uSunGlow;
vec3 seaHue() {
  vec3 env = mix(uFogColor, uHorizon, 0.5);
  return mix(env / max(dot(env, vec3(0.2126, 0.7152, 0.0722)), 1e-3), vec3(0.45, 1.15, 1.2), 0.12);
}
vec3 fogTint(vec3 dir) {
  float toward = max(dot(dir, normalize(uSunDir)), 1e-4);
  return uFogColor + uLightColor * (uSunGlow.x * pow(toward, uSunGlow.y) * (1.0 - uNight));
}
vec3 applyFog(vec3 color, vec3 worldPos, vec3 eyePos) {
  vec3 ray = worldPos - eyePos;
  float dist = length(ray);
  float heightFactor = exp(-max(worldPos.y - uSeaLevel, 0.0) * 0.012);
  float density = uFogDensity * (0.55 + 0.45 * heightFactor);
  // Fase 13 ("Mintlit"): além do exp² (o horizonte some), um termo linear fraco — a perspectiva aérea
  // que já desbota as copas a meia distância, como na referência.
  float k = density * dist;
  float f = 1.0 - exp(-k * k - k * 0.3);
  return mix(color, fogTint(ray / max(dist, 1e-4)), clamp(f, 0.0, 1.0));
}
`,
} satisfies Chunk;

/**
 * Vento único: `uWind` (x: força, y: rapidez; 1 e 1 é o vento calmo de sempre) vale para a
 * folhagem e para o capim, cada um com o seu balanço. `windClock` é o tempo do vento.
 */
const wind = {
  id: "wind",
  deps: ["atmo"],
  vertex: /* glsl */ `
uniform vec4 uWind;
float windClock() {
  return uTime * uWind.y;
}
// Folhagem: o deslocamento cresce com o peso do vértice, com fase pela posição da instância.
vec2 foliageWind(vec2 base, float weight, float strength, float scale) {
  float t = windClock();
  float phase = dot(base, vec2(0.131, 0.177));
  float gust = (0.6 + 0.4 * sin(t * 0.37 + phase * 0.2)) * scale;
  return vec2(sin(t * 1.7 + phase) * weight * strength * gust * uWind.x, cos(t * 1.3 + phase * 1.3) * weight * strength * 0.6 * gust * uWind.x);
}
// Capim: balanço por posição, crescendo com o quadrado da altura na lâmina.
vec2 grassWind(vec2 xz) {
  float t = windClock();
  float sway = (sin(t * 2.1 + xz.x * 0.35 + xz.y * 0.27) * 0.12 + sin(t * 3.7 + xz.x) * 0.04) * uWind.x;
  return vec2(sway, sway * 0.6);
}
`,
} satisfies Chunk;

/**
 * Limiar de pontilhado na tela (0 a 1, Bayer 4 × 4 pela célula do pixel): o sumiço perto, o de longe e a
 * troca de LOD descartam o pixel abaixo dele, como o "dithered LOD transition" dos motores sem TAA. Fixo na
 * tela (não pisca de um quadro para o outro).
 */
const dither = {
  id: "dither",
  deps: [],
  fragment: /* glsl */ `
const float BAYER4[16] = float[16](0.0, 8.0, 2.0, 10.0, 12.0, 4.0, 14.0, 6.0, 3.0, 11.0, 1.0, 9.0, 15.0, 7.0, 13.0, 5.0);
float ditherThreshold() {
  ivec2 cell = ivec2(gl_FragCoord.xy) & 3;
  return (BAYER4[cell.y * 4 + cell.x] + 0.5) / 16.0;
}
`,
} satisfies Chunk;

/**
 * Sumiço perto da câmera (`USE_NEAR_FADE`), em profundidade no espaço do olho: pontilhado Bayer
 * 4 × 4 entre `uNearFade.x` e `.y`; com o piso (`USE_NEAR_FADE_FLOOR`), a casca só acima dele. O
 * descarte vem depois de todas as derivadas.
 */
const nearFade = {
  id: "nearFade",
  deps: ["dither"],
  vertex: /* glsl */ `
#ifdef USE_NEAR_FADE
out float vViewDepth;
#endif
#ifdef USE_NEAR_FADE_FLOOR
out float vBaseHeight;
out float vLeaf;
#endif
`,
  fragment: /* glsl */ `
#ifdef USE_NEAR_FADE_FLOOR
uniform float uNearFadeFloor;
in float vBaseHeight;
in float vLeaf;
#endif
#ifdef USE_NEAR_FADE
uniform vec2 uNearFade;
in float vViewDepth;
void nearFade() {
  #ifdef USE_NEAR_FADE_FLOOR
    if (vLeaf < 0.5 && vBaseHeight < uNearFadeFloor) return;
  #endif
  if ((vViewDepth - uNearFade.x) / (uNearFade.y - uNearFade.x) < ditherThreshold()) discard;
}
#endif
`,
} satisfies Chunk;

/**
 * Troca de LOD e sumiço na distância (`USE_FAR_FADE`): `vLodFade.x` entra (0 → 1 entre `uFarFade.x` e
 * `.y`, m) e `.y` sai (1 → 0 entre `.z` e `.w`), pela distância no chão da câmera à base da instância.
 * Pontilhado complementar: quem sai fica com o pixel abaixo do limiar e quem entra com o de cima, então,
 * na mesma faixa, o LOD de perto e o de longe cobrem cada pixel uma vez só (sem buraco nem os dois).
 */
const farFade = {
  id: "farFade",
  deps: ["dither"],
  vertex: /* glsl */ `
#ifdef USE_FAR_FADE
uniform vec4 uFarFade;
out vec2 vLodFade;
#endif
`,
  fragment: /* glsl */ `
#ifdef USE_FAR_FADE
in vec2 vLodFade;
void farFade() {
  float b = ditherThreshold();
  if (b >= vLodFade.y || b < 1.0 - vLodFade.x) discard;
}
#endif
`,
} satisfies Chunk;

/** Paletas por instância (`USE_PALETTES`, F42): `palette` (0, 1 ou 2) escolhe `color`, `color1` ou `color2`. */
const palettes = {
  id: "palettes",
  deps: [],
  vertex: /* glsl */ `
#ifdef USE_PALETTES
in vec3 color1;
in vec3 color2;
in float palette;
vec3 paletteColor(vec3 base) {
  if (palette > 1.5) return color2;
  if (palette > 0.5) return color1;
  return base;
}
#endif
`,
} satisfies Chunk;

/**
 * Sombras-blob (F51): elipses suaves no chão sob jogadores e criaturas, sem draw call. `uBlobs[i]` =
 * (x, z, raio, altura dos pés), os `uBlobCount` mais perto da câmera; `uBlobStyle` = (força,
 * alongamento para longe da luz, altura em que some, oclusão do ambiente). A elipse se alonga para
 * longe do sol (mais com o sol baixo) e some com a distância vertical entre o chão e os pés (voadoras,
 * saltos). Laço de tamanho fixo (WebKit) com `break` na contagem; nada além de 52 m da câmera. Na
 * sombra fica só o ambiente (frio, nunca preto), um pouco ocluído (`blobShade`): sem a oclusão, ao
 * entardecer, com o sol já fraco, a sombra sumiria.
 */
const blobs = {
  id: "blobs",
  deps: ["atmo"],
  vertex: /* glsl */ `
uniform vec4 uBlobs[16];
uniform float uBlobCount;
uniform float uBlobLift[16];
uniform vec4 uBlobStyle;
`,
  fragment: /* glsl */ `
uniform vec4 uBlobs[16];
uniform float uBlobCount;
uniform float uBlobLift[16];
uniform vec4 uBlobStyle;
vec3 blobShade(vec3 lit, vec3 ambientOnly, float shadow) {
  return mix(lit, ambientOnly * (1.0 - uBlobStyle.w), shadow);
}
`,
} satisfies Chunk;

/** O cálculo da sombra-blob, no estágio de quem a recebe (fragmento do terreno, vértice do capim). */
const BLOB_SHADOW = /* glsl */ `
float blobShadow(vec3 p) {
  if (uBlobCount < 0.5 || uBlobStyle.x <= 0.0) return 0.0;
  vec2 fromEye = p.xz - cameraPosition.xz;
  if (dot(fromEye, fromEye) > 52.0 * 52.0) return 0.0;
  vec2 away = -uLightDir.xz;
  float lean = length(away);
  vec2 axis = lean > 1e-4 ? away / lean : vec2(1.0, 0.0);
  // No máximo 1,6×: a sombra do Guardião não chega à cópia (que nasce a 2,5 m ou mais).
  float stretch = 1.0 + uBlobStyle.y * min(lean / max(uLightDir.y, 0.2), 1.0);
  float shadow = 0.0;
  for (int i = 0; i < 16; i++) {
    if (float(i) >= uBlobCount) break;
    vec4 b = uBlobs[i];
    vec2 d = p.xz - b.xy;
    // Fora do alcance da elipse (r² > 1 garantido): nada a somar.
    float reach = b.z * (1.6 * stretch + 1.0);
    if (dot(d, d) > reach * reach) continue;
    float along = (dot(d, axis) - b.z * (stretch - 1.0) * 0.6) / stretch;
    float across = dot(d, vec2(-axis.y, axis.x));
    float r2 = (along * along + across * across) / max(b.z * b.z, 1e-6);
    // Recursos estáticos usam corte baixo: não projetar a copa sobre o fundo de um penhasco.
    float liftRange = uBlobLift[i] > 0.0 ? uBlobLift[i] : uBlobStyle.z;
    float lift = 1.0 - smoothstep(0.0, liftRange, abs(p.y - b.w));
    shadow = max(shadow, (1.0 - smoothstep(0.3, 1.0, r2)) * lift);
  }
  return shadow * uBlobStyle.x;
}
`;
const blobVertex = { id: "blobVertex", deps: ["blobs"], vertex: BLOB_SHADOW } satisfies Chunk;
const blobFragment = { id: "blobFragment", deps: ["blobs"], fragment: BLOB_SHADOW } satisfies Chunk;

/**
 * Máscara do contorno (F51, para o pós-processamento da F52): alfa = 1 − `uOutline` (por material)
 * onde a máscara está ligada (`uOutlineMask`, da atmosfera); o contorno vai onde o alfa é 0
 * (personagens, itens e estruturas). Desligada, o alfa é sempre 1: o WebKit mostra o alfa escrito
 * num canvas `alpha: false` (os personagens saíam pretos), então só um alvo RGBA de quem lê a
 * máscara a liga (as sondas; a F52). Só materiais opacos: os transparentes misturam o alfa com o que
 * está atrás.
 */
const outline = {
  id: "outline",
  deps: [],
  fragment: /* glsl */ `
uniform float uOutline;
uniform float uOutlineMask;
float outlineAlpha() {
  return 1.0 - uOutline * uOutlineMask;
}
`,
} satisfies Chunk;

/**
 * Brilho próprio com bloom (F52; o Médio tem bloom desde a F61): `uGlowGain` fica em 1 no canvas e
 * passa de 1 só enquanto o compositor desenha com bloom; aí os emissivos sobem à luminância dele (o limiar do
 * bloom é 1), seja qual for o matiz (`glowBoost`; a correção de cor do pós traz o pixel de volta ao
 * matiz), e o metal de pedra-de-raio, a cor-chave `uGlowKey` (xyz: a direção da cor linear; w: o
 * cosseno mínimo), vira emissivo (`glowKeyed`). Nada de programa novo: são uniforms.
 */
const glow = {
  id: "glow",
  deps: [],
  fragment: /* glsl */ `
uniform float uGlowGain;
uniform vec4 uGlowKey;
float glowBoost(vec3 albedo) {
  return uGlowGain > 1.0 ? uGlowGain / max(dot(albedo, vec3(0.2126, 0.7152, 0.0722)), 0.1) : 1.0;
}
float glowKeyed(vec3 color) {
  return uGlowGain > 1.0 && dot(normalize(color + 1e-4), uGlowKey.xyz) > uGlowKey.w ? 1.0 : 0.0;
}
`,
} satisfies Chunk;

/**
 * Dissolve (F21, `USE_DISSOLVE`): o corpo some em pedacinhos (células de ~17 cm no mundo) com a borda
 * acesa; `uDissolve` vai de 0 (inteiro) a 1 (sumido). Negativo, some igual pelo módulo mas sem a borda
 * (F55: o próprio boneco com a câmera colada nele). O descarte fica no fim do `main`. A cor da borda é a
 * de `uDissolveEdge` (rgb; F95): nos avatares e nas criaturas, o próprio objeto do `uStyleAccent` (o
 * visual de antes); nas famílias da F95, a cor de quem causou, sem mexer no rim e nas arestas.
 */
const dissolve = {
  id: "dissolve",
  deps: [],
  fragment: /* glsl */ `
#ifdef USE_DISSOLVE
uniform float uDissolve;
uniform vec4 uDissolveEdge;
float dissolveGrain(vec3 p) {
  return fract(sin(dot(floor(p * 6.0), vec3(12.9898, 78.233, 37.719))) * 43758.5453);
}
vec3 dissolveEdge(vec3 color, float grain) {
  // Malha neon (F60): a borda acende na cor do dono (o slot de quem morre, o tom da criatura; F95: de quem causou).
  vec3 edge = uDissolveEdge.rgb * 2.5 + 0.15;
  return uDissolve > 0.0 && grain < uDissolve + 0.08 ? mix(color, edge, 0.85) : color;
}
#endif
`,
} satisfies Chunk;

/**
 * Skinning pelos trechos do three (F49/F50: `SkinnedMesh` sem morphs): as declarações aqui e, no
 * `main`, `<skinbase_vertex>` e `<skinning_vertex>` sobre `transformed`, antes da instância e do
 * modelo. Só compila com `USE_SKINNING`, que o three liga sozinho num `SkinnedMesh`.
 */
const skin = { id: "skin", deps: [], vertex: ShaderChunk.skinning_pars_vertex } satisfies Chunk;


/**
 * Malha monocromática (Fase 8, F58/F59; guia em `docs/research/09-guia-malha-neon.md`, adaptado no
 * doc 09 §1), o núcleo comum: cinza pela luminância do albedo dentro da faixa do papel visual
 * (`uStyleRole`: cinza mín, cinza máx, —, rim), acento na máscara ou na peça inteira (`uStyleWhole`) e
 * rim de Fresnel na cor do dono (`uStyleAccent`). Facetas limpas, sem arestas nem nós (Fase 15, ADR
 * 0028: a referência `in-game.png`). Tudo por uniforms, nenhuma define.
 * `styleGray` só tira a cor (capim, céu, efeitos que não são de gameplay).
 */
const styleCore = {
  id: "styleCore",
  deps: [],
  fragment: /* glsl */ `
uniform vec4 uStyleRole;
uniform vec4 uStyleAccent;
uniform float uStyleWhole;
const vec3 STYLE_LUMA = vec3(0.2126, 0.7152, 0.0722);
vec3 styleGray(vec3 color) {
  return vec3(dot(color, STYLE_LUMA));
}
// Quanto da cor da LUZ atravessa o albedo neutro (pesquisa 11): 0 = o cinza puro da F59; 1 = a luz
// inteira. Contido: o sol quente e o preenchimento frio tingem o mundo cinza sem recolorir material.
const float STYLE_LIGHT_SAT = 0.35;
// Cinza do papel em fração perceptual de branco (o guia, §7): vira linear antes da luz. O albedo
// continua neutro (só a luminância dele escolhe o cinza), mas o matiz da luz (lit / albedo, por
// canal) atravessa em STYLE_LIGHT_SAT: o entardecer esquenta as facetas como nas referências.
vec3 styleCompose(vec3 lit, vec3 albedo, vec3 normal, vec3 view, float tint) {
  float la = max(dot(albedo, STYLE_LUMA), 1e-3);
  vec3 lightColor = lit / max(albedo, vec3(1e-3));
  float lightLuma = dot(lightColor, STYLE_LUMA);
  vec3 light = min(mix(vec3(lightLuma), lightColor, STYLE_LIGHT_SAT), vec3(1.25));
  float gray = pow(mix(uStyleRole.x, uStyleRole.y, sqrt(clamp(la, 0.0, 1.0))), 2.2);
  float fresnel = pow(1.0 - clamp(dot(normal, view), 0.0, 1.0), 3.5);
  // A máscara acende as costuras; arma e núcleo acendem inteiros.
  float mask = max(tint * 0.35, uStyleWhole);
  return gray * light + uStyleAccent.rgb * (uStyleAccent.w * mask + uStyleRole.w * fresnel);
}
`,
} satisfies Chunk;

/** O trecho da superfície (modelos e primitivas): a máscara `tint` da cor de vértice. */
const style = {
  id: "style",
  deps: ["styleCore"],
  vertex: /* glsl */ `
in float tint;
out float vTint;
void styleVertex() {
  vTint = tint;
}
`,
  fragment: /* glsl */ `
in float vTint;
vec3 styleOut(vec3 lit, vec3 albedo, vec3 normal, vec3 view) {
  return styleCompose(lit, albedo, normal, view, vTint);
}
`,
} satisfies Chunk;

/**
 * Eventos de energia (pesquisa 11): até 8 por quadro (`uEnergy[i]` = x, z, raio, força; a cor em
 * `uEnergyColor[i]`), preenchidos por `EnergyEvents`. `energyGlow` devolve a cor somada dos eventos
 * no ponto, com a soma limitada a 1 nas sobreposições (cores separadas em lugares separados se
 * preservam; dois ultimates juntos não estouram). Quem chama aplica um brilho fraco e local: o
 * evento acende o entorno, nunca tinge o chão inteiro. Laço fixo com `break` (WebKit).
 */
const energy = {
  id: "energy",
  deps: [],
  fragment: /* glsl */ `
uniform vec4 uEnergy[8];
uniform vec3 uEnergyColor[8];
uniform float uEnergyCount;
vec3 energyGlow(vec3 p) {
  vec3 glow = vec3(0.0);
  float total = 0.0;
  for (int i = 0; i < 8; i++) {
    if (float(i) >= uEnergyCount) break;
    vec4 e = uEnergy[i];
    float k = (1.0 - smoothstep(e.z * 0.35, e.z, distance(p.xz, e.xy))) * e.w;
    glow += uEnergyColor[i] * k;
    total += k;
  }
  return total > 1.0 ? glow / total : glow;
}
`,
} satisfies Chunk;

/**
 * A luz do anel do jogador local no chão (Fase 13): uma faixa acesa no raio do anel e um banho fraco
 * em volta, que somem com a distância vertical (o chão de um andar acima não acende).
 */
const ringLight = {
  id: "ringLight",
  deps: [],
  fragment: /* glsl */ `
uniform vec4 uRingLight;
uniform vec3 uRingColor;
vec3 ringLight(vec3 p) {
  if (uRingLight.w <= 0.0) return vec3(0.0);
  float d = distance(p.xz, uRingLight.xz);
  float band = exp(-abs(d - uRingLight.w) * 7.0);
  float fill = (1.0 - smoothstep(0.0, uRingLight.w * 2.4, d)) * 0.12;
  float h = 1.0 - smoothstep(0.2, 1.4, abs(p.y - uRingLight.y));
  return uRingColor * (band * 0.55 + fill) * h;
}
`,
} satisfies Chunk;

/**
 * Sombra do sol (Fase 13, G3; \`render/sun-shadow.ts\`): 1 no sol, até 1 − força na sombra. O ponto sai
 * pela normal (\`uSunShadowParams.y\` m) antes de projetar; 5 amostras no fragmento (uma no vértice do
 * capim), cada uma já um PCF 2 × 2 em hardware (comparação com filtro linear); some na borda da área.
 */
const SUN_SHADOW_COMMON = /* glsl */ `
uniform sampler2DShadow uSunShadowMap;
uniform mat4 uSunShadowMatrix;
uniform vec4 uSunShadowParams;
// O ponto no mapa (xy) e a profundidade (z), com a borda da área (w: 0 fora, 1 dentro).
vec4 sunShadowCoord(vec3 worldPos, vec3 normal) {
  vec4 sc = uSunShadowMatrix * vec4(worldPos + normal * uSunShadowParams.y, 1.0);
  vec3 c = sc.xyz * 0.5 + 0.5;
  vec2 edge = min(c.xy, 1.0 - c.xy);
  return vec4(c.xy, c.z - 0.0003, smoothstep(0.0, 0.08, min(edge.x, edge.y)) * step(c.z, 1.0));
}
// Uma amostra (já um PCF 2 × 2 em hardware): o capim, no vértice.
float sunShadowFast(vec3 worldPos, vec3 normal) {
  if (uSunShadowParams.w < 0.5) return 1.0;
  vec4 c = sunShadowCoord(worldPos, normal);
  if (c.w <= 0.0) return 1.0;
  return 1.0 - (1.0 - textureLod(uSunShadowMap, c.xyz, 0.0)) * uSunShadowParams.z * c.w;
}
`;
const sunShadow = {
  id: "sunShadow",
  deps: [],
  vertex: SUN_SHADOW_COMMON,
  fragment: /* glsl */ `
${SUN_SHADOW_COMMON}
// Cinco amostras em cruz rodada (cada uma um PCF 2 × 2): a borda macia sem pesar no fragmento.
float sunShadow(vec3 worldPos, vec3 normal) {
  if (uSunShadowParams.w < 0.5) return 1.0;
  vec4 c = sunShadowCoord(worldPos, normal);
  if (c.w <= 0.0) return 1.0;
  float t = uSunShadowParams.x * 1.5;
  // Nível 0 explícito: o desvio do branch deixaria as derivadas implícitas indefinidas.
  float s = textureLod(uSunShadowMap, c.xyz, 0.0);
  s += textureLod(uSunShadowMap, vec3(c.xy + vec2(0.81, 0.36) * t, c.z), 0.0);
  s += textureLod(uSunShadowMap, vec3(c.xy + vec2(-0.36, 0.81) * t, c.z), 0.0);
  s += textureLod(uSunShadowMap, vec3(c.xy + vec2(-0.81, -0.36) * t, c.z), 0.0);
  s += textureLod(uSunShadowMap, vec3(c.xy + vec2(0.36, -0.81) * t, c.z), 0.0);
  return 1.0 - (1.0 - s / 5.0) * uSunShadowParams.z * c.w;
}
`,
} satisfies Chunk;

/**
 * A arrebentação (Fase 15): ondas que correm para a praia, crescem ao raso, quebram em espuma e sobem a
 * areia (o espraiado), e a areia molhada que seca entre uma onda e outra. A mesma conta no vértice do
 * mar (altura), no fragmento do mar (espuma) e no fragmento do terreno (areia molhada): concordam.
 * A frente segue a distância à costa (\`uShore\`, a do plano, 8 m): as cristas ficam paralelas à praia
 * (a refração de graça), com um desvio lento por ruído para a linha não sair reta. Espelho em TS:
 * \`surf.ts\` (testes).
 */
const surf = {
  id: "surf",
  deps: ["atmo", "noise"],
  vertex: SURF,
  fragment: SURF,
} satisfies Chunk;

export const CHUNKS = { atmo, hash, noise, ramp3, keyFill, rimNight, fogGlow, wind, dither, nearFade, farFade, palettes, blobs, blobVertex, blobFragment, outline, glow, dissolve, skin, style, styleCore, energy, surf, ringLight, sunShadow } satisfies Record<ChunkId, Chunk>;

/** Junta os trechos de um estágio, dependências antes, cada um uma vez só. */
export function assemble(stage: Stage, ids: readonly ChunkId[]): string {
  const seen = new Set<ChunkId>();
  const out: string[] = [];
  const visit = (id: ChunkId, path: readonly ChunkId[]): void => {
    if (seen.has(id)) return;
    if (path.includes(id)) throw new Error(`dependência circular: ${[...path, id].join(" → ")}`);
    const chunk: Chunk = CHUNKS[id];
    for (const dep of chunk.deps) visit(dep, [...path, id]);
    seen.add(id);
    const code = chunk[stage];
    if (code !== undefined) out.push(`// @chunk ${id}${code.startsWith("\n") ? "" : "\n"}${code}`);
  };
  for (const id of ids) visit(id, []);
  return out.join("\n");
}
